/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

type RowColBaseProps = {
	fill?: boolean;
	relative?: boolean;
	center?: boolean;
	justifyCenter?: boolean;
	justifyStart?: boolean;
	justifyEnd?: boolean;
	alignCenter?: boolean;
	alignBaseline?: boolean;
	spaceBetween?: boolean;
	children?: React.ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[prop: string]: any;
};

type RowColScrollable = RowColBaseProps & {
	styleOuter?: React.CSSProperties;
	styleInner?: React.CSSProperties;
	classNameOuter?: string;
	classNameInner?: string;
};

type RowColNonScrollable = RowColBaseProps & {
	noShrink?: boolean;
	noScroll?: boolean;
	style?: React.CSSProperties;
	className?: string;
};

const getStyle = (props: RowColBaseProps) => ({
	...props.relative ? {
		position: 'relative',
	} : {},
	...props.center ? {
		justifyContent: 'center',
		alignItems: 'center',
	} : {},
	...props.alignCenter ? {
		alignItems: 'center',
	} : {},
	...props.alignBaseline ? {
		alignItems: 'baseline',
	} : {},
	...props.justifyCenter ? {
		justifyContent: 'center',
	} : {},
	...props.justifyStart ? {
		justifyContent: 'flex-start',
	} : {},
	...props.justifyEnd ? {
		justifyContent: 'flex-end',
	} : {},
	...props.spaceBetween ? {
		justifyContent: 'space-between',
		alignItems: 'center',
	} : {},
	...props.noShrink ? {
		flexShrink: 0,
	} : {},
	...props.noScroll ? {
		overflow: 'hidden',
	} : {},
});

export const Col: React.FC<RowColNonScrollable> = (props) => {
	const {
		fill,
		relative,
		center,
		alignCenter,
		alignBaseline,
		justifyCenter,
		justifyStart,
		justifyEnd,
		spaceBetween,
		noShrink,
		noScroll,
		style,
		children,
		...rest
	} = props;

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				...fill ? {
					flex: 1,
					width: '100%',
				} : {},
				...getStyle(props),
				...style,
			} as React.CSSProperties}
			{...rest}
		>
			{children}
		</div>
	);
};

export const ScrollCol: React.FC<RowColScrollable> = (props) => {
	const {
		fill,
		styleInner,
		styleOuter,
		classNameInner,
		classNameOuter,
		children,
		...rest
	} = props;

	return (
		<div
			style={{
				overflow: 'auto',
				...fill ? {
					flex: 1,
					width: '100%',
				} : {},
				...styleOuter,
			}}
			className={classNameOuter}
		>
			<Col
				{...{
					style: styleInner,
					className: classNameInner,
					...rest,
				}}
			>
				{children}
			</Col>
		</div>
	);
};

export const Row: React.FC<RowColNonScrollable> = (props) => {
	const {
		fill,
		relative,
		center,
		alignCenter,
		alignBaseline,
		justifyCenter,
		justifyStart,
		justifyEnd,
		spaceBetween,
		noShrink,
		noScroll,
		style,
		children,
		block,
		...rest
	} = props;

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'row',
				...fill ? {
					flex: 1,
					height: '100%',
				} : {},
				...getStyle(props),
				...style,
			} as React.CSSProperties}
			{...rest}
		>
			{children}
		</div>
	);
};

export const ScrollRow: React.FC<RowColScrollable> = (props) => {
	const {
		fill,
		styleInner,
		styleOuter,
		classNameInner,
		classNameOuter,
		children,
		...rest
	} = props;

	return (
		<div
			style={{
				overflow: 'auto',
				...fill ? {
					flex: 1,
					width: '100%',
				} : {},
				...styleOuter,
			}}
			className={classNameOuter}
		>
			<Row
				{...{
					style: styleInner,
					className: classNameInner,
					...rest,
				}}
			>
				{children}
			</Row>
		</div>
	);
};

type FillProps = {
	zIndex?: number;
	col?: boolean;
	style?: React.CSSProperties;
	children: React.ReactNode;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[prop: string]: any;
};

export const Fill: React.FC<FillProps> = ({
	zIndex, col, style, children, ...rest
}) => (
	<div
		style={{
			position: 'absolute',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			display: 'flex',
			...typeof zIndex !== 'undefined' ? {
				zIndex,
			} : {},
			...col ? {
				flexDirection: 'column',
			} : {},
			...style,
		}}
		{...rest}
	>
		{children}
	</div>
);
